import React from 'react';

import { IconButton } from '@components/button/Button';
import Tooltip from '@components/tooltip/Tooltip';
import { SelectedFileItem } from '@features/lead-management/types/leadFileUpload';
import {
  formatDisplayDate,
  formatTime
} from '@features/lead-management/utils/common';

import LeadFileCard from '../LeadFileCard';
import NotesInput from './NotesInput.';

import useCanEditLead from '../../hooks/useCanEditLead';

type DocumentItem = {
  id?: string;
  file_name: string;
  file_size: string;
  url?: string;
};

type DocumentMap = Record<string, DocumentItem>;

type NoteWithDocumentMap = {
  id: string;
  note: string;
  created_at: string;
  updated_at?: string;
  owner: {
    name: string;
  };
  documents: DocumentMap;
};

interface NoteItemProps {
  note: NoteWithDocumentMap;
  index: number;
  totalNotes: number;
  editingNoteId: string | null;
  editText: string;
  editHasInteracted: boolean;
  editFiles: SelectedFileItem[];
  editTextareaRef: React.RefObject<HTMLTextAreaElement>;
  editFileInputRef: React.RefObject<HTMLInputElement>;
  onStartEdit: (note: NoteWithDocumentMap) => void;
  onDeleteClick: (noteId: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onSetEditText: (text: string) => void;
  onSetEditHasInteracted: (value: boolean) => void;
  onAddEditFiles: (files: FileList) => void;
  onRemoveEditItem: (fileId: string) => void;
  onAttachEditFile: () => void;
  onViewFile: (file: SelectedFileItem, noteId: string) => void;
  onDownloadFile: (file: SelectedFileItem, noteId: string) => void;
}

const NoteItem: React.FC<NoteItemProps> = ({
  note,
  index,
  totalNotes,
  editingNoteId,
  editText,
  editHasInteracted,
  editFiles,
  editTextareaRef,
  editFileInputRef,
  onStartEdit,
  onDeleteClick,
  onCancelEdit,
  onSaveEdit,
  onSetEditText,
  onSetEditHasInteracted,
  onAddEditFiles,
  onRemoveEditItem,
  onAttachEditFile,
  onViewFile,
  onDownloadFile
}) => {
  const canEditLead = useCanEditLead();

  const getDocuments = (documents: DocumentItem[]) =>
    documents.map((document: DocumentItem) => ({
      ...document,
      name: document.file_name || '',
      sizeLabel: document.file_size || '',
      status: 'completed' as any,
      url: document.url
    })) as SelectedFileItem[];

  const isNoteEdited = (item: NoteWithDocumentMap) => {
    if (item.updated_at) {
      const createdDate = new Date(item?.created_at);
      const updatedDate = new Date(item?.updated_at);
      const timeDifference = Math.abs(
        updatedDate.getTime() - createdDate.getTime()
      );
      const threshold = 5000; // 5 seconds threshold

      if (timeDifference > threshold) {
        const formattedDate = formatDisplayDate(item?.updated_at);
        const formattedTime = formatTime(item?.updated_at);
        return `${formattedDate} ${formattedTime}`;
      }
    }
    return '';
  };

  const getTimeStamp = (item: NoteWithDocumentMap) => {
    if (!item.created_at) return '';
    const isEdited = isNoteEdited(item);
    const timestamp = isEdited ? item?.updated_at : item?.created_at;
    const formattedDate = formatDisplayDate(timestamp || '');
    const formattedTime = formatTime(timestamp || '');
    return `${formattedDate} ${formattedTime}`;
  };

  const showEditedMark = (item: NoteWithDocumentMap) => {
    const isEdited = isNoteEdited(item);
    if (isEdited) {
      return <span className="text-sm text-gray-500">(edited)</span>;
    }
    return null;
  };

  const renderDocuments = (item: NoteWithDocumentMap) => {
    if (Object.values(item.documents).length > 0) {
      return (
        <div className="flex w-fit min-w-[50%] flex-col gap-2">
          <LeadFileCard
            files={getDocuments(Object.values(note.documents))}
            showViewButton
            showDownloadButton
            showClose={false}
            handleViewFile={(file) => onViewFile(file, note.id)}
            handleDownload={(file) => onDownloadFile(file, note.id)}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div key={note.id} className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-gray-900">
              {note.owner.name}
            </span>

            <span className="ml-1 text-sm text-gray-500">
              {getTimeStamp(note)}
            </span>
            {showEditedMark(note)}
          </div>

          <div className="flex items-center gap-2">
            <IconButton
              iconName="edit-grey"
              variant="transparent"
              size="none"
              className="!p-1 disabled:opacity-50"
              iconSize="24"
              handleClick={() => onStartEdit(note)}
              data-tooltip-id={`edit-icon-${note.id}`}
              disabled={!canEditLead}
            />
            <Tooltip
              id={`edit-icon-${note.id}`}
              type="text"
              tooltipText="Edit"
            />

            <IconButton
              iconName="trash"
              variant="transparent"
              size="none"
              className="!p-1 text-error40 disabled:opacity-50"
              iconSize="medium"
              handleClick={() => onDeleteClick(note.id)}
              data-tooltip-id={`delete-icon-${note.id}`}
              disabled={!canEditLead}
            />
            <Tooltip
              id={`delete-icon-${note.id}`}
              type="text"
              tooltipText="Delete"
            />
          </div>
        </div>

        {/* Note Content */}
        {editingNoteId === note.id ? (
          <div className="flex flex-col gap-3 rounded-xl bg-[#F9F9F9] p-6">
            <NotesInput
              noteText={editText}
              setNoteText={onSetEditText}
              hasInteracted={editHasInteracted}
              setHasInteracted={onSetEditHasInteracted}
              textareaRef={editTextareaRef}
              fileInputRef={editFileInputRef}
              newFiles={editFiles}
              addFiles={onAddEditFiles}
              removeItem={(file) => onRemoveEditItem(file.id)}
              handleAttachFile={onAttachEditFile}
              handleCancel={onCancelEdit}
              handleSave={onSaveEdit}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="break-all text-base text-gray-800">{note.note}</p>
            {renderDocuments(note)}
          </div>
        )}
      </div>
      {index !== totalNotes - 1 && <div className="h-px bg-[#E5E5E5]" />}
    </>
  );
};

export default NoteItem;
